// ============================================================================
// CloudCost redesign — design tokens (imported from CloudCost.dc.html design)
// Single source of truth for the new visual language. Front-end only.
// ============================================================================

export const T = {
  // typography
  font: "'Lato', system-ui, -apple-system, 'Segoe UI', sans-serif",

  // surfaces / background
  bg: '#EEF1F6',
  surface: '#FFFFFF',
  surfaceAlt: '#FCFDFF',
  surfaceMuted: '#F9FBFE',

  // text
  text: '#16233A',
  textMut: '#3D4A5C',
  textSoft: '#5A6472',
  textFaint: '#7A8497',
  textFaint2: '#8A94A6',
  textFaint3: '#9BA6B7',

  // primary (blue)
  primary: '#1C71E3',
  primaryHover: '#1558B0',
  primaryTint: '#EAF2FD',
  primaryLight: '#53A6F0',

  // borders / dividers
  border: '#E7ECF3',
  borderSide: '#E4E9F1',
  borderInput: '#E1E7F0',
  divider: '#EEF1F6',

  // semantic
  success: '#16A55C',
  successTint: '#E5F6EC',
  warn: '#F28E2C',
  warnDark: '#B26A12',
  warnTint: '#FDEFDE',
  warnTint2: '#FBF2D8',
  danger: '#E23D3D',
  dangerTint: '#FDEBEB',
  ai: '#7A4FD6',
  aiTint: '#F1EBFB',
  neutral: '#8A94A6',
  neutralTint: '#F3F6FA',
  neutralGrey: '#C6CFDC',

  // shape
  radius: 14,
  radiusSm: 9,
  radiusXs: 7,
  cardShadow: '0 1px 2px rgba(16,35,58,.04)',
  popShadow: '0 12px 40px rgba(16,35,58,.16)',
} as const;

// Tone → { color, tint } for pills / stat icons / badges
export type Tone = 'primary' | 'success' | 'warn' | 'danger' | 'ai' | 'neutral';

export const TONES: Record<Tone, { color: string; tint: string }> = {
  primary: { color: T.primary, tint: T.primaryTint },
  success: { color: T.success, tint: T.successTint },
  warn: { color: T.warnDark, tint: T.warnTint2 },
  danger: { color: T.danger, tint: T.dangerTint },
  ai: { color: T.ai, tint: T.aiTint },
  neutral: { color: T.textSoft, tint: T.neutralTint },
};

// Ordered categorical palette for charts (matches design accents)
export const DS_CHART_COLORS = [
  '#1C71E3', // blue
  '#F28E2C', // orange
  '#16A55C', // green
  '#7A4FD6', // purple
  '#53A6F0', // light blue
  '#E23D3D', // red
  '#B26A12', // amber
  '#C6CFDC', // neutral
];

// Map a status label (recommendations / attestation / disputes / budgets) to a tone
export function statusTone(status?: string): Tone {
  const s = (status || '').toLowerCase();
  if (['active', 'confirmed', 'on track', 'healthy', 'approved', 'open', 'resolved'].includes(s)) return 'success';
  if (['pending', 'unresolved', 'at risk', 'overdue'].some((k) => s.includes(k))) return 'warn';
  if (['rejected', 'breached', 'over forecast', 'over budget', 'failed', 'error'].some((k) => s.includes(k))) return 'danger';
  if (['implemented'].includes(s)) return 'primary';
  return 'neutral';
}
